/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import {Helmet} from 'react-helmet';
import {styled} from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import {
  Button,
  ButtonVariant,
  ButtonWidth,
} from '@practicegenius/pg-ui/src/base/Material/components/Button';
import {ProgressBar} from '../ProgressBar';
import {BREAKPOINTS} from '../../constants/layout';

const SectionHeading = styled('h2')({
  fontSize: '24px',
});

const Content = styled('div')({
  flex: 'auto',
  margin: '20px 0 20px 0',
  display: 'flex',
  flexDirection: 'column',
});

const BottomText = styled('div')({
  fontSize: '14px',
  textAlign: 'center',
});

const Wrapper = styled('div')({
  marginBottom: '20px',
});

const TitleWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '16px',
});

const P = styled('p')({
  margin: 0,
});

const HeaderIconButton = styled(IconButton)({
  /* Only display for small screens */
  [`@media (min-width: ${BREAKPOINTS.SMALL})`]: {
    display: 'none',
  },
});

const HeaderIconPlaceholder = styled('div')(({theme}) => ({
  width: theme.spacing(5),
}));

const ScreenTitle = styled('h3')(({theme}) => ({
  flex: 1,
  color: theme.palette.common.black,
  textAlign: 'center',
  fontSize: '26px',
  fontWeight: 300,
  textTransform: 'none',
  margin: 0,
  position: 'relative',
}));

const Card = styled('div')({
  background: '#fff',
  padding: '30px 45px 45px 45px',
  minHeight: '600px',
  width: '560px',
  margin: '50px auto 20px auto',
  display: 'flex',
  flexDirection: 'column',

  border: '1px solid #e5e5ea',

  [`@media (max-width: ${BREAKPOINTS.SMALL})`]: {
    border: 'none',
    width: '100%',
    height: 'auto',
    padding: '20px 20px 0 20px',
    margin: 0,
  },

  [`@media (min-width: ${BREAKPOINTS.SMALL}) and (max-width: ${BREAKPOINTS.MEDIUM})`]:
    {
      maxWidth: '560px',
      width: '90%',
    },
});

const Hr = styled('div')({
  height: '1px',
  flex: 1,
  backgroundColor: '#e5e5ea',
});

const Outer = styled('div')({
  background: '#fff',

  [`@media (max-width: ${BREAKPOINTS.SMALL})`]: {
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 100,
    overflowY: 'scroll',
  },
});

const ButtonContainer = styled('div')<{
  containsMultipleButtons: boolean | undefined;
}>(({containsMultipleButtons}) => ({
  display: 'flex',
  flexDirection: 'row-reverse',
  justifyContent: containsMultipleButtons ? 'space-between' : 'center',

  [`@media (max-width: ${BREAKPOINTS.SMALL})`]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

type CustomWidth = 'extraWide';
type CustomHeight = 'tall';

export type ExtendedButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  'variant'
> & {
  bottomMargin?: boolean;
  variant?: 'outlined';
  /** Specify a custom height variant not defined in the base Button */
  customWidth?: CustomWidth;
  /** Specify a custom width variant not defined in the base Button */
  customHeight?: CustomHeight;
};

type Props = {
  helmetTitle: string;
  headerTitle: string;
  title?: string;
  instructions?: React.ReactNode;
  children: React.ReactNode;
  actionButtons?: Array<ExtendedButtonProps>;
  afterButtons?: React.ReactNode;
  bottomText?: React.ReactNode;
  disableModalMode?: boolean;
  cancelProps?: React.ComponentProps<'a'> | React.ComponentProps<'button'>;
  hideBackButton?: boolean;
};

export function BaseScreen({
  cancelProps,
  divider,
  hideBackButton = false,
  ...props
}: Props & {
  divider?: React.ReactElement<typeof ProgressBar>;
}) {
  return (
    <Outer>
      <Helmet title={`${props.helmetTitle} | PatientRewardsHub.com`} />
      <Card>
        <Wrapper>
          <TitleWrapper>
            {hideBackButton ? (
              <HeaderIconPlaceholder />
            ) : (
              <HeaderIconButton
                title="Go Back"
                onClick={() => {
                  window.history.back();
                }}
              >
                <ArrowBackIcon />
              </HeaderIconButton>
            )}
            <ScreenTitle>{props.headerTitle}</ScreenTitle>
            {cancelProps ? (
              <HeaderIconButton
                // @ts-ignore
                component={'href' in cancelProps ? 'a' : 'button'}
                {...cancelProps}
              >
                <CloseIcon />
              </HeaderIconButton>
            ) : (
              <HeaderIconPlaceholder />
            )}
          </TitleWrapper>
          {divider || <Hr />}
        </Wrapper>
        {props.title && <SectionHeading>{props.title}</SectionHeading>}
        {Boolean(props.instructions) && <P>{props.instructions}</P>}
        <Content>{props.children}</Content>
        {props.actionButtons && (
          <ButtonContainer
            containsMultipleButtons={Boolean(
              props.actionButtons && props.actionButtons.length > 1
            )}
          >
            {props.actionButtons.map((buttonProps, i) => (
              <StyledButton key={i} {...buttonProps}>
                <Box>{buttonProps.children}</Box>
              </StyledButton>
            ))}
          </ButtonContainer>
        )}
        {props.afterButtons}
      </Card>
      {Boolean(props.bottomText) && <BottomText>{props.bottomText}</BottomText>}
    </Outer>
  );
}

export function ProgressScreen({
  children,
  progress,
  ...props
}: Props & {
  progress: number;
}) {
  return (
    <BaseScreen {...props} divider={<ProgressBar progress={progress} />}>
      {children}
    </BaseScreen>
  );
}

const StyledButton: React.FC<ExtendedButtonProps> = props => {
  const {
    bottomMargin = true,
    variant,
    color,
    customHeight,
    customWidth,
    ...rest
  } = props;
  return (
    <Button
      width={ButtonWidth.WIDE}
      variant={variant as ButtonVariant | undefined}
      color={color}
      loadingIndicatorColor="primary"
      {...rest}
      sx={theme => ({
        minHeight: 40,
        width: customWidth === 'extraWide' ? 300 : undefined,
        height: customHeight === 'tall' ? 55 : undefined,
        lineHeight: 'normal',
        [theme.breakpoints.down('sm')]: {
          marginBottom: bottomMargin ? '20px' : undefined,
          width: '100%',
        },
      })}
    />
  );
};
